import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useClickOutside } from "../../utils/hooks/useClickOutside";

interface Props {
  label: string;
  options: string[];
  selectedOptions: string[];
  onSelect: (option: any) => void;
  contentClassname?: string;
  buttonClassname?: string;
}

const DropdownFilter = ({
  selectedOptions = [],
  onSelect,
  options = [],
  label = "",
  contentClassname = "",
  buttonClassname = "h-8",
}: Props) => {
  /** State */
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({ top: 0, right: 0 });
  /** Refs */
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const getClickOutsideNodes = useCallback(
    () => [buttonRef.current, dropdownRef.current] as (HTMLElement | null)[],
    []
  );
  useClickOutside(() => setIsOpen(false), undefined, getClickOutsideNodes);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownStyle({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, [isOpen]);

  const dropdownContent = isOpen && (
    <div
      ref={(el) => {
        (dropdownRef as React.MutableRefObject<HTMLDivElement | null>).current =
          el;
      }}
      className={`fixed w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-[9999] ${contentClassname}`}
      style={{
        top: dropdownStyle.top,
        right: dropdownStyle.right,
      }}
      role="menu"
    >
      <div className="py-1">
        <form className="px-4 py-2">
          {options.map((option) => (
            <label key={option} className="flex items-center space-x-2 mt-2">
              <input
                type="checkbox"
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:outline-none focus:ring-0"
                // @ts-ignore
                checked={selectedOptions.includes(option)}
                onChange={() => onSelect(option)}
              />
              <span className="text-gray-700 text-sm">{option}</span>
            </label>
          ))}
        </form>
      </div>
    </div>
  );

  return (
    <div className="relative inline-block text-left">
      <button
        ref={buttonRef}
        onClick={toggleDropdown}
        className={`px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm focus:outline-none focus:ring-0 ${buttonClassname}`}
      >
        {label}
        <svg
          className="-mr-1 ml-2 h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.293 9.293a1 1 0 011.414 0L10 12.586l3.293-3.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default DropdownFilter;
